import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TaskDropdown } from '../TaskDropdown'
import { MovePicker, type MovePickerProps } from './MovePicker'
import { readLastMoveSpace, rememberMoveSpace } from './lastMoveSpace'
import { ShortcutBoxBody } from '../TaskMenu/TaskMenuShortcutBox'
import '../TaskMenu/TaskMenuShortcutBox.scss'

export interface MoveShortcutBoxProps extends Omit<MovePickerProps, 'title' | 'initialSpace' | 'onSpaceChange'> {
  /** Titel der angefassten Aufgabe — für „„Rasen mähen“ verschieben“. */
  taskTitle: string
  /** Wie viele wandern würden; ab zwei heißt der Titel „3 Aufgaben verschieben“. */
  countMoving: () => Promise<number>
  /** Gewählt, angelegt oder geschlossen. */
  onClose: () => void
  /**
   * `'row'` (Vorgabe): an einem Punkt unten links an der Zeile. `'trigger'`: Der Knopf `trigger` öffnet
   * und schließt das Panel, es geht darüber auf — so an der **Listen-Werkzeug-Pille** (#291).
   */
  anchor?: 'row' | 'trigger'
  trigger?: React.ReactElement
  /** Bei `anchor="trigger"` offen/zu; sonst steht das Panel offen, solange es im Baum ist. */
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /** Eine Zeile über dem Panel — „Wirkt auf 2 von 3 …“ an der Pille. */
  notice?: string
  /** Ohne Einblenden — beim Wechsel von einem Kasten zum nächsten (Überfahren in der Pille). */
  instant?: boolean
}

/**
 * **„Verschieben …“ an der Zeile** (#257, #263) — per Rechtsklick, ⇧⌘M oder aus dem Menü „Aufgaben“
 * geht das Panel unten links an der Zeile auf, an demselben Anker und in derselben Hülle wie die
 * Kästen von S, T & Co. (`TaskMenuShortcutBox`).
 *
 * Tasten darin laufen im React-Baum nicht zur Zeile hoch — außer Esc, das die Hülle am `document` hört.
 *
 * @example
 * <MoveShortcutBox taskTitle="Rasen mähen" countMoving={() => moveMenu.countMoving(id)} startSpace="home"
 *   current={null} onSelect={move} onCreateProject={moveToNewProject} onClose={close} />
 */
export const MoveShortcutBox: React.FC<MoveShortcutBoxProps> = ({
  taskTitle, countMoving, onSelect, onCreateProject, onClose, anchor = 'row', trigger, open = true, onOpenChange, notice, instant = false, ...picker
}) => {
  const { t } = useTranslation()
  // Erst gezählt, dann betitelt — sonst stünde kurz der Titel der einen Aufgabe da und spränge auf die Zahl.
  const [count, setCount] = useState<number | null>(null)
  // Am Knopf öffnet dasselbe Panel mehrmals — jedes Mal neu gezählt, ohne den alten Titel aufblitzen zu lassen.
  const [prevOpen, setPrevOpen] = useState(open)
  if (open !== prevOpen) {
    setPrevOpen(open)
    if (open) setCount(null)
  }
  const countRef = useRef(countMoving)
  countRef.current = countMoving
  // Am Knopf steht das Panel auch zu im Baum — gezählt wird beim Öffnen.
  useEffect(() => {
    if (!open) return
    let alive = true
    void countRef.current().then(n => { if (alive) setCount(n) })
    return () => { alive = false }
  }, [open])
  const title = count == null ? '' : count > 1 ? t('taskItem.move.titleMultiple', { count }) : t('taskItem.move.title', { title: taskTitle })

  const atTrigger = anchor === 'trigger' && trigger
  const dropdown = (
    <TaskDropdown
      open={open}
      onOpenChange={(next) => {
        if (onOpenChange) onOpenChange(next)
        else if (!next) onClose()
      }}
      side={atTrigger ? 'top' : 'bottom'}
      // Am Knopf mittig über ihm (Sven, 08.10.2026); an der Zeile links an ihrem Anfang.
      align={atTrigger ? 'center' : 'start'}
      // Am Knopf ohne Fläche dahinter: Die Nachbarn in der Pille bleiben erreichbar (#291).
      backdrop={!atTrigger}
      instant={instant}
      trigger={atTrigger ? trigger : <span className="task-menu-shortcut__anchor" aria-hidden="true" />}
    >
      <ShortcutBoxBody notice={notice}>
        <MovePicker
          {...picker}
          title={title}
          // Der zuletzt gewählte Lebensbereich (#279) — die Hülle merkt ihn, das Panel nicht.
          initialSpace={readLastMoveSpace()}
          onSpaceChange={rememberMoveSpace}
          onSelect={(target, place, project) => { onClose(); onSelect(target, place, project) }}
          onCreateProject={(project) => { onClose(); onCreateProject(project) }}
        />
      </ShortcutBoxBody>
    </TaskDropdown>
  )
  if (atTrigger) return dropdown
  return (
    <span
      className="task-menu-shortcut task-menu-shortcut--row"
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => { if (e.key !== 'Escape') e.stopPropagation() }}
    >
      {dropdown}
    </span>
  )
}
